import React, { useState } from 'react';
import { BadgeCheckIcon, GraduationCapIcon, InfoIcon, MailCheckIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { LEVEL_DOES_NOT_ASSERT, LEVEL_LABEL, LEVEL_MEANING, STATE_LABEL, STATUS_LABEL, badgeVisible, isSensitiveStatus } from '../../utils/verification';
import type { AcademicStanding } from '../../types/verification';

/**
 * The academic standing chip other people see.
 *
 * One component so the same record cannot read as "Verified student" on one
 * surface and a bare tick on another. It never renders an unqualified
 * checkmark: the label always names what was established.
 */
export function StandingChip({
  standing,
  size = 'sm'
}: {
  standing: AcademicStanding | null;
  size?: 'sm' | 'md';
}) {
  const [open, setOpen] = useState(false);

  // No record at all is its own statement, and worth making.
  if (!standing) {
    return <Badge tone="neutral" icon={<InfoIcon className="h-4 w-4" data-id="element-5738" data-mp="zvfdv91a6nal" />} plain data-id="element-5737" data-mp="zst3z2qx3vp7">
        Academic information not verified
      </Badge>;
  }
  const live = badgeVisible(standing.state);
  const alumni = standing.status === 'alumni' || standing.status === 'graduated';
  const label = !live ? STATE_LABEL[standing.state] : alumni ? `${standing.institution} alumni` : LEVEL_LABEL[standing.level];
  const icon = !live ? <InfoIcon className="h-4 w-4" data-id="element-5739" data-mp="d1h4ofhucvw0" /> : alumni ? <GraduationCapIcon className="h-4 w-4" data-id="element-5740" data-mp="d321ycvjisxk" /> : standing.level === 'email_verified' ? <MailCheckIcon className="h-4 w-4" data-id="element-5741" data-mp="h3vs7cwggjtm" /> : <BadgeCheckIcon className="h-4 w-4" data-id="element-5742" data-mp="m25o5pq07oga" />;
  return <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center rounded-md" aria-label={`${label}. What this means.`} data-id="element-5743" data-mp="m0uifkd006mj">
        <Badge tone={live ? alumni ? 'brand' : 'ok' : 'warn'} icon={icon} data-id="element-5744" data-mp="3picphpky59lw">
          {label}
        </Badge>
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title={label} description="What Campuzon has established, and what it has not." width={size === 'md' ? 'lg' : 'md'} data-id="element-5745" data-mp="3ufw62w549yh0">
        <dl className="space-y-3" data-id="element-5746" data-mp="1afkd0t6m3p7r">
          <Row label="Institution" value={standing.institution} data-id="element-5747" data-mp="1aer53ovx2to1" />
          {standing.campus && <Row label="Campus" value={standing.campus} data-id="element-5748" data-mp="16dyt4nvnuao2" />}
          {standing.programme && <Row label="Programme" value={standing.programme} data-id="element-5749" data-mp="iocrltrtfjcw" />}
          {standing.academicLevel && <Row label="Level" value={standing.academicLevel} data-id="element-5750" data-mp="dpjhmbbqmahh" />}
          {!isSensitiveStatus(standing.status) && standing.status !== 'unknown' && <Row label="Status" value={STATUS_LABEL[standing.status]} data-id="element-5751" data-mp="rq41late2cck" />}
          <Row label="Confirmed by" value={standing.confirmedBy ?? 'Nobody — this is a claim'} data-id="element-5752" data-mp="mp6h2rkwp67w" />
        </dl>

        <div className="mt-4 rounded-md border border-line bg-canvas p-4" data-id="element-5753" data-mp="2336envwa4jrz">
          <p className="max-w-[64ch] text-body-sm text-ink" data-id="element-5754" data-mp="1y668lkzfjh0v">{LEVEL_MEANING[standing.level]}</p>
          <p className="mt-2 max-w-[64ch] text-body-sm text-ink-muted" data-id="element-5755" data-mp="26lvrons1divy">
            What it does not mean: {LEVEL_DOES_NOT_ASSERT[standing.level]}
          </p>
        </div>

        <p className="mt-3 max-w-[64ch] text-body-sm text-ink-muted" data-id="element-5756" data-mp="1wo7l90l2imxy">
          Verification is one signal. It says nothing about how someone behaves in a transaction, a
          tenancy or a conversation — those have their own records.
        </p>
      </Modal>
    </>;
}

/**
 * The academic line itself: institution, programme, level. Replaces the ad-hoc
 * "programme · level" strings that carried no indication of who had checked
 * them. Fields the register does not hold are simply absent.
 */
export function AcademicLine({
  standing,
  fallback
}: {
  standing: AcademicStanding | null;
  fallback?: string;
}) {
  if (!standing) {
    return <p className="text-body-sm text-ink-muted" data-id="element-5757" data-mp="38a6a2syherwr">
        {fallback ?? 'No academic record on file'}
        <span className="text-ink-subtle" data-id="element-5758" data-mp="3d422lfvz7mkj"> — entered by this person, unchecked</span>
      </p>;
  }
  const parts = [standing.programme, standing.academicLevel].filter(Boolean) as string[];
  const established = badgeVisible(standing.state) && standing.level !== 'email_verified';
  return <>
      <p className="text-body-sm text-ink" data-id="element-5759" data-mp="1fghzsqza6nlx">{parts.join(' · ') || standing.institution}</p>
      <p className="text-caption text-ink-subtle" data-id="element-5760" data-mp="1ai6pkf52qb36">
        {standing.institution}
        {standing.campus ? ` · ${standing.campus}` : ''}
        {established ? '' : ' · not independently confirmed'}
      </p>
    </>;
}
function Row({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 border-b border-line pb-2.5 last:border-0 last:pb-0" data-id="element-5761" data-mp="1yd9hivz6cunv">
      <dt className="text-body-sm text-ink-muted" data-id="element-5762" data-mp="1tdwtw90hbjk8">{label}</dt>
      <dd className="text-body-sm text-ink" data-id="element-5763" data-mp="2qwpwwcvy16q7">{value}</dd>
    </div>;
}